import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, Gift, Heart } from "lucide-react";
import { BankDonate } from "@/components/donate/BankDonate";
import { PERKS, PERKS_SHORT } from "@/lib/perks";
import { useAuth } from "@/context/AuthContext";
import { VipPreviewButton } from "@/components/donate/VipPreview";

function BoxCountdown() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/mystery-box").then(({ data }) => setM(data)).catch(() => {}); }, []);
  if (!m?.supporter || m.days_left == null) return null;
  const pct = Math.round(((m.day_in_cycle || 0) / m.cycle) * 100);
  return (
    <Link to="/community-unterstuetzen#mystery-box" className="block rounded-2xl bg-white/5 border border-[#c99a3c]/30 p-4 space-y-2 hover:bg-white/10 transition-colors" data-testid="supporter-box-countdown">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-rose-500/30"><Gift className="w-5 h-5 text-white" /></span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-amber-100/80">Nächste Mystery-Box per Post</p>
          <p className="font-display text-xl font-extrabold text-white" data-testid="supporter-box-days">in {m.days_left} {m.days_left === 1 ? "Tag" : "Tagen"} 🎁</p>
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-fuchsia-400" style={{ width: `${Math.max(4, pct)}%` }} /></div>
      <p className="text-xs text-amber-100/60">Tag {m.day_in_cycle || 0} von {m.cycle} am Stück{m.boxes?.length ? ` · schon ${m.boxes.length} ${m.boxes.length === 1 ? "Box" : "Boxen"} verdient` : ""}</p>
      {m.ends_before_next && <p className="text-xs font-bold text-amber-300" data-testid="supporter-box-warning">Achtung: Deine Unterstützung endet vorher am {new Date(m.ends_before_next).toLocaleDateString("de-DE")} – verlängere rechtzeitig, damit die Box nicht verfällt.</p>}
    </Link>
  );
}

function ThanksCard({ user }) {
  const until = user.supporter_until ? new Date(user.supporter_until) : null;
  const days = until ? Math.max(0, Math.ceil((until - Date.now()) / 86400000)) : null;
  const pct = days == null ? 100 : Math.min(100, Math.round((days / 30) * 100));
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 sm:p-6 vip-lux border border-[#c99a3c]/60 shadow-xl shadow-rose-300/40 fade-up" data-testid="supporter-thanks-card">
      <span className="contest-shine" />
      <div className="relative space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-pink-200 via-rose-400 to-amber-300 flex items-center justify-center shadow-lg shadow-rose-500/30"><Heart className="w-6 h-6 text-[#2c0f1b]" /></span>
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] vip-gold-text">Projekt Unterstützer</p>
            <p className="font-display text-xl sm:text-2xl font-extrabold text-white leading-tight">Danke, {(user.name || "").split(" ")[0]}! 💖</p>
            <p className="text-sm text-amber-100/80 mt-1">Du hältst unsere Community am Leben – dafür leuchtest du mit ganz viel Herz.</p>
          </div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-[#c99a3c]/30 p-4 space-y-2">
          <div className="flex items-end justify-between gap-2">
            <p className="text-sm text-amber-100/80">Deine Unterstützer-Zeit</p>
            <p className="font-display text-xl sm:text-2xl font-extrabold vip-gold-text whitespace-nowrap" data-testid="supporter-days-left">{days == null ? "unbegrenzt" : `noch ${days} ${days === 1 ? "Tag" : "Tage"}`}</p>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-rose-400 via-pink-300 to-amber-200" style={{ width: `${pct}%` }} /></div>
          {until && <p className="text-xs text-amber-100/60" data-testid="supporter-until">bis {until.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</p>}
        </div>
        <BoxCountdown />
        <div className="flex flex-wrap gap-2">
          <Link to="/community-unterstuetzen" data-testid="supporter-thanks-extend" className="flex-1 min-w-[140px] h-11 rounded-full vip-gold-metal font-extrabold text-sm inline-flex items-center justify-center">Verlängern</Link>
          <Link to={`/u/${user.user_id}`} data-testid="supporter-thanks-profile" className="flex-1 min-w-[140px] h-11 rounded-full border-2 border-rose-300 text-rose-100 font-bold text-sm inline-flex items-center justify-center">Mein Herz-Profil</Link>
        </div>
      </div>
    </div>
  );
}

export function DonateFeedCard() {
  const { user } = useAuth();
  if (user?.supporter) return <ThanksCard user={user} />;
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 sm:p-6 vip-lux border border-[#c99a3c]/60 shadow-xl shadow-rose-300/40 fade-up" data-testid="donate-feed-card">
      <span className="contest-shine" />
      {[[8, 10, 0], [92, 8, .7], [86, 55, 1.3], [4, 62, 1.9], [60, 3, .4]].map(([x, y, d]) => <span key={`${x}-${y}`} className="contest-spark" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${d}s` }} />)}
      <div className="relative space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-pink-200 via-rose-400 to-amber-300 flex items-center justify-center shadow-lg shadow-rose-500/30"><Heart className="w-6 h-6 text-[#2c0f1b]" /></span>
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] vip-gold-text">Projekt Unterstützer</p>
            <p className="font-display text-xl sm:text-2xl font-extrabold text-white leading-tight">Unterstütze uns & werde Teil des Herz-Clubs 💖</p>
          </div>
        </div>
        <p className="text-sm text-amber-100/80">Jede Spende hilft uns, die App, Gewinne und neue Funktionen zu finanzieren – ohne Werbung, ohne Datenverkauf. Als Dankeschön bekommst du:</p>
        <ul className="grid grid-cols-2 gap-2" data-testid="donate-feed-perks">
          {PERKS.map(([Icon, title], i) => (
            <li key={title} title={title} className={`flex items-center gap-2 rounded-2xl bg-white/5 border border-[#c99a3c]/25 px-2.5 py-2 text-xs sm:text-sm font-semibold text-amber-50 ${i === PERKS.length - 1 ? "col-span-2 justify-center" : ""}`}>
              <Icon className="w-4 h-4 shrink-0 text-rose-200" /> {PERKS_SHORT[i]}
            </li>
          ))}
        </ul>
        <VipPreviewButton className="w-full" testId="donate-feed-preview" />
        <BankDonate lux testId="donate-feed" />
        <Link to="/community-unterstuetzen" data-testid="donate-feed-more" className="flex items-center justify-center gap-1 text-sm font-bold text-rose-100 hover:text-white">Alle Vorteile ansehen <ChevronRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
